"use client"

import { useState, useEffect, useRef } from "react"
import { X, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react"

interface FlashCard {
  id: string
  question: string
  answer: string
  category: string
}

interface FlashCardsModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  cards: FlashCard[]
}

export function FlashCardsModal({ isOpen, onClose, title, cards }: FlashCardsModalProps) {
  const [currentCardIndex, setCurrentCardIndex] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)

  const currentCard = cards[currentCardIndex]
  const hasNextCard = currentCardIndex < cards.length - 1
  const hasPreviousCard = currentCardIndex > 0

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [isOpen, onClose])

  // Reset body overflow
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Reset card state when changing cards
  useEffect(() => {
    setIsFlipped(false)
  }, [currentCardIndex])

  const handleFlip = () => {
    if (isAnimating) return
    setIsAnimating(true)
    setIsFlipped(!isFlipped)
    setTimeout(() => setIsAnimating(false), 300)
  }

  const handleNextCard = () => {
    if (hasNextCard) {
      setCurrentCardIndex(currentCardIndex + 1)
    }
  }

  const handlePreviousCard = () => {
    if (hasPreviousCard) {
      setCurrentCardIndex(currentCardIndex - 1)
    }
  }

  const handleReset = () => {
    setCurrentCardIndex(0)
    setIsFlipped(false)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
     
      {/* Blurry Backdrop */}
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal container */}
      <div 
        ref={modalRef}
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl h-[80vh] flex flex-col relative z-10 transform transition-all duration-300 scale-100"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
              <p className="text-sm text-slate-500">
                Card {currentCardIndex + 1} of {cards.length}
              </p>
            </div>
          </div>
          
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors min-h-[44px]"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          {/* Flash Card */}
          <div className="w-full max-w-md perspective-1000">
            <div
              className={`relative w-full h-64 transition-transform duration-500 preserve-3d cursor-pointer ${
                isFlipped ? 'rotate-y-180' : ''
              }`}
              onClick={handleFlip}
            >
              {/* Front of card */}
              <div className={`absolute inset-0 w-full h-full backface-hidden ${
                isFlipped ? 'opacity-0' : 'opacity-100'
              } transition-opacity duration-300`}>
                <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg p-8 flex flex-col items-center justify-center text-white">
                  <div className="text-center">
                    <span className="text-blue-200 text-sm font-medium mb-4 block">
                      Question
                    </span>
                    <h3 className="text-2xl font-bold mb-4 leading-tight">
                      {currentCard?.question}
                    </h3>
                    <p className="text-blue-200 text-sm">
                      Click to reveal answer
                    </p>
                  </div>
                </div>
              </div>

              {/* Back of card */}
              <div className={`absolute inset-0 w-full h-full backface-hidden rotate-y-180 ${
                isFlipped ? 'opacity-100' : 'opacity-0'
              } transition-opacity duration-300`}>
                <div className="w-full h-full bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-lg p-8 flex flex-col items-center justify-center text-white">
                  <div className="text-center">
                    <span className="text-emerald-200 text-sm font-medium mb-4 block">
                      Answer
                    </span>
                    <h3 className="text-2xl font-bold mb-4 leading-tight">
                      {currentCard?.answer}
                    </h3>
                    <p className="text-emerald-200 text-sm">
                      Click to see question
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Progress indicator */}
          <div className="flex justify-center mt-8 mb-6">
            <div className="flex gap-2">
              {cards.map((_, index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentCardIndex
                      ? 'bg-blue-500'
                      : 'bg-slate-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Instructions */}
          <p className="text-slate-600 text-center text-sm mb-8">
            Click on the card to flip between question and answer
          </p>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between p-6 border-t border-slate-200 bg-slate-50">
          <button
            onClick={handlePreviousCard}
            disabled={!hasPreviousCard}
            className="flex items-center gap-3 px-6 py-3 text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
          >
            <ChevronLeft className="w-5 h-5" />
            Previous
          </button>

          <div className="text-sm text-slate-600 text-center">
            <span className="font-medium">{currentCard?.category}</span>
          </div>

          <button
            onClick={handleNextCard}
            disabled={!hasNextCard}
            className="flex items-center gap-3 px-6 py-3 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
          >
            Next
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
}